import type { AppEdge } from '@/edges/types';
import type { AppNode, AppNodeTypeWithData } from '@/nodes/types';
import type { AppFlowObjectKnown } from '@/util/AppFlowObjectKnown';
import { buildDataKeySummary } from '@/util/dataKeyUtils';
import { AppFlowDao } from '@rock/db-admin';
import { nodeDataSchemas, type AppFlowObject, type AppFlowParams, type SpaceId } from '@rock/types';
import { shallowEqual } from '@rock/util';
import { atom, type Atom } from 'jotai';
import { atomFamily, loadable } from 'jotai/utils';
import { keyBy } from 'lodash';
import * as z from 'zod';

const appFlowDao = new AppFlowDao();

export const addingNodeState = atom<AppNodeTypeWithData>();
export type AppFlowParamsWithNodeId = AppFlowParams & { nodeId: string };

export const selectedFlowIdState = atomFamily((_: AppFlowParams) => atom(''), shallowEqual);
const EMPTY_FLOW: AppFlowObject = {
    object: 'app-flow',
    schema: 1,
    id: '',
    name: '',
    space: '' as SpaceId,
    nodes: [],
    edges: [],
    nextId: 1,
};

export const currentFlowState = atomFamily(
    // Read-only, and narrowed: the DAO types the doc as `AppFlowObject` (nodes/edges as unknown),
    // while every reader here works the known node/edge unions. The cast narrows the READ — it must
    // not make the atom writable again, which is what let two callers write whole flow docs.
    (params: AppFlowParams) =>
        appFlowDao.draftFlowAtom(params.spaceId, selectedFlowIdState(params)) as Atom<
            Promise<AppFlowObjectKnown | undefined>
        >,
    shallowEqual,
);

/**
 * Replaces a draft's whole graph in one write — "Pull Guide" (copy the live guide back over the
 * draft) and the Import-JSON dialog. Merges the three content fields the way `nodesState` and its
 * siblings merge one, so the draft's identity (`id`, `space`, `object`, `schema`) is never restated
 * from a value the editor happens to be holding.
 */
export const replaceFlowContentState = atomFamily(
    (params: AppFlowParams) =>
        atom(null, async (get, _set, content: Pick<AppFlowObject, 'nodes' | 'edges' | 'nextId'>) => {
            const flowId = get(selectedFlowIdState(params));
            if (flowId) {
                await appFlowDao.updateFlow(get, params.spaceId, flowId, content);
            }
        }),
    shallowEqual,
);

export const nodesState = atomFamily((params: AppFlowParams) => {
    const flowAtom = currentFlowState(params);
    return atom(
        async (get) => ((await get(flowAtom))?.nodes || EMPTY_FLOW.nodes) as AppNode[],
        async (get, _set, nodes: AppNode[]) => {
            const flowId = get(selectedFlowIdState(params));
            if (flowId) {
                await appFlowDao.updateFlow(get, params.spaceId, flowId, {
                    nodes: nodes.filter((n) => !!n.type),
                });
            }
        },
    );
}, shallowEqual);

export const edgesState = atomFamily((params: AppFlowParams) => {
    const flowAtom = currentFlowState(params);
    return atom(
        async (get) => ((await get(flowAtom))?.edges || EMPTY_FLOW.edges) as AppEdge[],
        async (get, _set, edges: AppEdge[]) => {
            const flowId = get(selectedFlowIdState(params));
            if (flowId) {
                await appFlowDao.updateFlow(get, params.spaceId, flowId, {
                    edges: edges.filter((e) => !!e.type),
                });
            }
        },
    );
}, shallowEqual);

export const nextIdState = atomFamily((params: AppFlowParams) => {
    const flowAtom = currentFlowState(params);
    return atom(null, async (get, _set) => {
        const flowId = get(selectedFlowIdState(params));
        if (flowId) {
            const flow = await get(flowAtom);
            const nextId = (flow?.nextId || EMPTY_FLOW.nextId) + 1;
            await appFlowDao.updateFlow(get, params.spaceId, flowId, {
                nextId: nextId,
            });
            return nextId;
        }
    });
}, shallowEqual);

const nodesByIdState = atomFamily(
    (params: AppFlowParams) => atom(async (get) => keyBy(await get(nodesState(params)), 'id')),
    shallowEqual,
);

const nodeByIdState = atomFamily(
    (params: AppFlowParamsWithNodeId) => atom(async (get) => (await get(nodesByIdState(params)))[params.nodeId]),
    shallowEqual,
);

export const selectedNodeState = atomFamily((params: AppFlowParams) => {
    const currentNodes = nodesState(params);
    return atom(
        async (get) => {
            const selectedNodes = (await get(currentNodes)).filter((n) => n.selected);
            if (selectedNodes.length === 1) {
                return selectedNodes[0];
            }
        },
        async (get, set, selectedNodeId?: string) => {
            const nodes = await get(currentNodes);
            await set(
                currentNodes,
                nodes.map((n) => ({
                    ...n,
                    selected: n.id === selectedNodeId,
                })),
            );
        },
    );
}, shallowEqual);

export const selectedEdgeState = atomFamily((params: AppFlowParams) => {
    const currentEdges = edgesState(params);
    return atom(
        async (get) => {
            const selectedEdges = (await get(currentEdges)).filter((n) => n.selected);
            if (selectedEdges.length === 1) {
                return selectedEdges[0];
            }
        },
        async (get, set, selectedEdgeId?: string) => {
            const edges = await get(currentEdges);
            await set(
                currentEdges,
                edges.map((n) => ({
                    ...n,
                    selected: n.id === selectedEdgeId,
                })),
            );
        },
    );
}, shallowEqual);

export const nodeValidationState = atomFamily(
    (params: AppFlowParamsWithNodeId) =>
        loadable(
            atom(async (get) => {
                const node = await get(nodeByIdState(params));
                if (node?.type) {
                    const schema = nodeDataSchemas[node.type];
                    if (schema) {
                        try {
                            schema.parse(node.data);
                        } catch (error) {
                            if (error instanceof z.ZodError) {
                                return error.issues.map((issue) => {
                                    const path = issue.path.join('.');
                                    return `${path}: ${issue.message}`;
                                });
                            }
                        }
                    }
                }
                return [];
            }),
        ),
    shallowEqual,
);

export const dataKeySummaryState = atomFamily(
    (params: AppFlowParams) =>
        atom(async (get) => {
            const nodes = (await get(nodesState(params))) as AppNode[];
            return buildDataKeySummary(nodes);
        }),
    shallowEqual,
);

export const producedDataKeysState = atomFamily(
    (params: AppFlowParams) =>
        atom(async (get) => {
            const summary = await get(dataKeySummaryState(params));
            return [...summary.values()].flatMap((entry) => entry.producers);
        }),
    shallowEqual,
);

export const disconnectedDataKeysState = atomFamily(
    (params: AppFlowParams) =>
        loadable(
            atom(async (get) => {
                const summary = await get(dataKeySummaryState(params));
                return [...summary.entries()]
                    .filter(([_, entry]) => entry.producers.length === 0)
                    .flatMap(([_, entry]) => entry.consumers);
            }),
        ),
    shallowEqual,
);
